/**
 * A trained line tagger, and running it.
 *
 * Small on purpose: a few dense layers over each row's features and its
 * neighbours', run here in plain TypeScript so the phone, the server and the
 * training tools all get the same answer from the same file. A receipt of
 * 80 rows is a few hundred thousand multiply-adds - well under a millisecond's
 * worth of work on a phone - so there is nothing for Core ML to win yet.
 *
 * The file is JSON: the roles and feature version it was trained on, the
 * neighbour window, and per layer its weights as base64 little-endian float32
 * in row-major order (`out × in`). `tools/receipt-tagger/train.py` writes it.
 */

import { FEATURE_SIZE, FEATURE_VERSION } from './features.js';
import { TAG_ROLES, type TagRole } from './roles.js';

export const TAGGER_FORMAT = 'kvitto-tagger';

export interface TaggerLayerFile {
  in: number;
  out: number;
  activation: 'relu' | 'linear';
  weights: string;
  bias: string;
}

export interface TaggerModelFile {
  format: typeof TAGGER_FORMAT;
  /** Names this model, so a device knows whether it already has it. */
  version: string;
  featureVersion: number;
  roles: string[];
  /** Rows on each side the model sees with a row. */
  window: number;
  layers: TaggerLayerFile[];
  /** What the trainer measured, for the record. */
  metrics?: Record<string, unknown>;
}

interface Layer {
  in: number;
  out: number;
  activation: 'relu' | 'linear';
  weights: Float32Array;
  bias: Float32Array;
}

export interface TaggerModel {
  version: string;
  window: number;
  layers: Layer[];
}

export interface RowTag {
  role: TagRole;
  /** The chosen role's probability, 0..1. */
  confidence: number;
}

export class TaggerModelError extends Error {}

const BASE64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const BASE64_INDEX = new Map([...BASE64].map((char, index) => [char, index]));

/** Base64 to float32, without `atob` or `Buffer`, which not every runtime here has. */
function floats(base64: string, expected: number, name: string): Float32Array {
  const clean = base64.replace(/[^A-Za-z0-9+/]/g, '');
  const bytes = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let byte = 0;
  for (let i = 0; i + 1 < clean.length; i += 4) {
    const a = BASE64_INDEX.get(clean[i]!)!;
    const b = BASE64_INDEX.get(clean[i + 1]!)!;
    const c = BASE64_INDEX.get(clean[i + 2] ?? 'A') ?? 0;
    const d = BASE64_INDEX.get(clean[i + 3] ?? 'A') ?? 0;
    const triple = (a << 18) | (b << 12) | (c << 6) | d;
    if (byte < bytes.length) bytes[byte++] = (triple >> 16) & 0xff;
    if (byte < bytes.length) bytes[byte++] = (triple >> 8) & 0xff;
    if (byte < bytes.length) bytes[byte++] = triple & 0xff;
  }
  if (bytes.length !== expected * 4) {
    throw new TaggerModelError(`${name}: ${bytes.length / 4} values, expected ${expected}`);
  }
  const view = new DataView(bytes.buffer);
  const out = new Float32Array(expected);
  for (let i = 0; i < expected; i += 1) out[i] = view.getFloat32(i * 4, true);
  return out;
}

/** A model file checked and decoded. Throws {@link TaggerModelError} for one this code cannot run. */
export function loadTaggerModel(file: unknown): TaggerModel {
  const model = file as Partial<TaggerModelFile> | null;
  if (!model || model.format !== TAGGER_FORMAT) throw new TaggerModelError('not a tagger model');
  if (model.featureVersion !== FEATURE_VERSION) {
    throw new TaggerModelError(`trained on features v${model.featureVersion}, this build computes v${FEATURE_VERSION}`);
  }
  if (!Array.isArray(model.roles) || model.roles.join() !== TAG_ROLES.join()) {
    throw new TaggerModelError('trained on another list of roles');
  }
  const window = model.window;
  if (typeof window !== 'number' || !Number.isInteger(window) || window < 0 || window > 8) {
    throw new TaggerModelError('bad window');
  }
  if (!Array.isArray(model.layers) || model.layers.length === 0) throw new TaggerModelError('no layers');

  let width = FEATURE_SIZE * (2 * window + 1);
  const layers = model.layers.map((layer, index): Layer => {
    if (layer.in !== width) throw new TaggerModelError(`layer ${index} takes ${layer.in}, expected ${width}`);
    if (layer.activation !== 'relu' && layer.activation !== 'linear') {
      throw new TaggerModelError(`layer ${index}: unknown activation`);
    }
    width = layer.out;
    return {
      in: layer.in,
      out: layer.out,
      activation: layer.activation,
      weights: floats(layer.weights, layer.in * layer.out, `layer ${index} weights`),
      bias: floats(layer.bias, layer.out, `layer ${index} bias`),
    };
  });
  if (width !== TAG_ROLES.length) throw new TaggerModelError(`ends in ${width} outputs, expected ${TAG_ROLES.length}`);
  return { version: String(model.version ?? 'unnamed'), window, layers };
}

/** Each row's role, from its features (`rowFeatures`). */
export function tagRows(model: TaggerModel, features: readonly Float32Array[]): RowTag[] {
  return features.map((_, row) => {
    let input = new Float32Array(FEATURE_SIZE * (2 * model.window + 1));
    for (let offset = -model.window; offset <= model.window; offset += 1) {
      const neighbour = features[row + offset];
      if (neighbour) input.set(neighbour, (offset + model.window) * FEATURE_SIZE);
    }
    for (const layer of model.layers) {
      const output = new Float32Array(layer.out);
      for (let o = 0; o < layer.out; o += 1) {
        let sum = layer.bias[o]!;
        const base = o * layer.in;
        for (let i = 0; i < layer.in; i += 1) {
          const value = input[i]!;
          if (value !== 0) sum += layer.weights[base + i]! * value;
        }
        output[o] = layer.activation === 'relu' && sum < 0 ? 0 : sum;
      }
      input = output;
    }
    const max = Math.max(...input);
    const exp = Array.from(input, (value) => Math.exp(value - max));
    const total = exp.reduce((a, b) => a + b, 0);
    let best = 0;
    for (let i = 1; i < exp.length; i += 1) if (exp[i]! > exp[best]!) best = i;
    return { role: TAG_ROLES[best]!, confidence: exp[best]! / total };
  });
}
